import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Store } from '@shared/core/utils';
import { MailNotifierApiService } from './mail-notifier-api.service';
import { EmailItemList, EmailsUnreadCount } from '../types';

class Actions {
  loadUnreadCount: Subject<void>;
  loadEmails: Subject<void>;
}

const initialState = {
  unreadCount: 0 as EmailsUnreadCount,
  emails: null as EmailItemList,
};

@Injectable({
  providedIn: 'root',
})
export class MailNotifierService {
  private store = new Store<typeof initialState>(initialState);
  private store$ = this.store.asReadonly();

  private actions: Actions = {
    loadUnreadCount: new Subject<void>(),
    loadEmails: new Subject<void>(),
  };

  constructor(private headerApi: MailNotifierApiService) {
    this.initLoadUnreadCount();
    this.initLoadEmails();
  }

  getUnreadCount(): Observable<EmailsUnreadCount> {
    return this.store$.select('unreadCount');
  }

  getEmails(): Observable<EmailItemList> {
    return this.store$.select('emails');
  }

  refreshEmails(): void {
    this.actions.loadEmails.next();
  }

  refreshUnreadCount(): void {
    this.actions.loadUnreadCount.next();
  }

  private initLoadUnreadCount(): void {
    this.actions.loadUnreadCount
      .asObservable()
      .pipe(switchMap(() => this.loadUnreadCount()))
      .subscribe();
  }

  private initLoadEmails(): void {
    this.actions.loadEmails
      .asObservable()
      .pipe(switchMap(() => this.loadEmails()))
      .subscribe();
  }

  private loadUnreadCount(): Observable<EmailsUnreadCount> {
    return this.headerApi.getCount().pipe(
      tap(unreadCount =>
        this.store.update(state => ({
          ...state,
          unreadCount,
        })),
      ),
    );
  }

  private loadEmails(): Observable<EmailItemList> {
    return this.headerApi.getEmails().pipe(
      tap(emails =>
        this.store.update(state => ({
          ...state,
          emails,
        })),
      ),
    );
  }
}
